import { useContext } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faDownload, faEye, faEyeSlash, faImage, faStar, faTrashAlt,
} from '@fortawesome/pro-light-svg-icons'

import {
  Box,
  Button,
  FileUploadZone,
  Form,
  Image,
  ModalContext,
  SidebarModal,
  StatusBadge,
  Text,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCurrentUser from '@hooks/useCurrentUser'
import useImage from '@hooks/useImage'
import useImageLibrary from '@hooks/useImageLibrary'
import useUppyImageUpload from '@hooks/useUppyImageUpload'

import handleCallbackAction from '@functions/handleCallbackAction'
import { confirmDelete } from '@functions/entity'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

const afterUploadAction = (closeModal) => {
  toast('Image Created Successfully')
  closeModal()
}

const CreateOrEditImageModal = (props) => {
  const { callbacks, modalKey = 'CreateOrEditImageModal', showModal } = props
  const {
    closeModal,
    setCoverImage,
  } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { callbacks: modalPayloadCallbacks, image: initImage } = modalPayload
  const { afterDeleteAction } = modalPayloadCallbacks || {}

  const {
    callbacks: {
      deleteImage: deleteFn,
      setCoverImage: setCoverFn,
      updateImage: updateFn,
    },
    deleting,
    image,
    isPlan,
    resolution: {
      label,
    },
    updating,
  } = useImage(initImage)

  const {
    file_file_name,
    id,
    image_type,
    imageable_id,
    imageable_type,
    original_url,
    private: isPrivate,
    provider_id,
    square_url,
  } = image

  const { height, width } = digObject(image, 'size.original', {})

  const uppy = useUppyImageUpload(
    {
      callbacks: {
        afterAction: () => afterUploadAction(closeModal),
      },
      image_type,
      imageable_id,
      imageable_type,
      provider_id,
      requestOptions: defaultRequestOptions.image,
    },
    [image_type, imageable_id, imageable_type, provider_id],
  )

  const coverImagePayload = {
    callbacks: {
      action: setCoverFn,
      afterAction: closeModal,
    },
    requestOptions: {
      ...defaultRequestOptions.image,
      include_image_library_owner: true,
      include_team_cover_image: true,
    },
    toastText: 'Images Updated',
  }

  const updateImagePrivacyPayload = {
    callbacks: {
      action: updateFn,
    },
    entityParams: { private: !isPrivate },
    requestOptions: defaultRequestOptions.image,
    toastText: 'Image Updated',
  }

  const updateImageTypePayload = {
    callbacks: {
      action: updateFn,
    },
    entityParams: { image_type: image_type === 'photo' ? 'plan' : 'photo' },
    requestOptions: defaultRequestOptions.image,
    toastText: 'Image Updated',
  }

  const confirmDeletePayload = {
    callbacks: {
      afterAction: () => {
        if (afterDeleteAction) afterDeleteAction()
        closeModal()
      },
      deleteFn,
    },
    strings: {
      confirmString: 'Are you sure?',
      entityName: 'Image',
    },
  }

  const {
    editable,
    imageLibrary,
  } = useImageLibrary({ id: imageable_type === 'ImageLibrary' ? image.imageable_id : null })

  // Cover images are normally sorted with index 0 being the cover.
  // In Image Libraries there is a cover_image_id that corresponds to the image.id
  const isCoverImage = image.sort === 0 || imageLibrary.cover_image_id === image.id
  const showCoverImageButton = !isCoverImage && imageable_type !== 'Agency' && imageable_type !== 'Campaign' && editable

  const currentUser = useCurrentUser()
  const { isProviderUser } = currentUser

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Create'} titleSecondLine="Image" />

      <SidebarModal.Content>
        <Form>
          {!id && (
            <Form.Field label="* Upload File">
              <FileUploadZone uppy={uppy} />
            </Form.Field>
          )}

          {id && (
            <>
              <Image
                borderRadius={5}
                boxProps={{ backgroundSize: isPlan ? 'contain' : 'cover' }}
                height={230}
                url={isPlan ? original_url : square_url}
              />

              {editable && imageable_type === 'Campaign' && (
                <Form.Field label={isProviderUser ? 'Image Type' : 'Status'} marginTop="large">

                  <Box flexDirection="row" alignItems="center">
                    {!isProviderUser && (
                      <Button
                        buttonStyle="secondaryUtility"
                        icon={isPrivate ? <FontAwesomeIcon icon={faEyeSlash} /> : <FontAwesomeIcon icon={faEye} />}
                        onClick={() => handleCallbackAction(updateImagePrivacyPayload)}
                        size="medium"
                        style={{ marginRight: 4, width: 'calc(50% - 4px)' }}
                      >
                        {isPrivate ? 'Private' : 'Public'}
                      </Button>
                    )}

                    <Button
                      buttonStyle="secondaryUtility"
                      icon={<FontAwesomeIcon icon={faImage} />}
                      onClick={() => handleCallbackAction(updateImageTypePayload)}
                      size="medium"
                      style={{
                        marginLeft: isProviderUser ? 0 : 4,
                        textTransform: 'capitalize',
                        width: isProviderUser ? '100%' : 'calc(50% - 4px)',
                      }}
                    >
                      {image_type}
                    </Button>
                  </Box>
                </Form.Field>
              )}

              <Form.Field label="File Name" marginTop="large">
                <Text variant="ellipsis" fontSize="small">
                  {file_file_name}
                </Text>
              </Form.Field>

              <Form.Field label="Dimensions" marginTop="large">
                <Text variant="ellipsis" fontSize="small">
                  {`${width} x ${height} px`}
                </Text>
              </Form.Field>

              {imageable_type === 'Campaign' && (
                <Form.Field label="Quality" marginTop="large">
                  <StatusBadge color={label === 'Low' ? 'red' : 'orange'} ghost text={label} />
                </Form.Field>
              )}

              <Form.Field label="More Options" marginTop="large">
                {editable && (
                  <Button
                    buttonStyle="secondaryUtility"
                    icon={<FontAwesomeIcon icon={faTrashAlt} />}
                    loading={deleting}
                    onClick={() => confirmDelete(confirmDeletePayload)}
                    size="medium"
                  >
                    Delete Image
                  </Button>
                )}

                <Button
                  as="a"
                  buttonStyle="secondaryUtility"
                  href={image.original_url}
                  icon={<FontAwesomeIcon icon={faDownload} />}
                  marginTop="medium"
                  size="medium"
                  target="_blank"
                >
                  Download Original
                </Button>

                <Button
                  as="a"
                  buttonStyle="secondaryUtility"
                  href={image.large_url}
                  icon={<FontAwesomeIcon icon={faDownload} />}
                  marginTop="medium"
                  size="medium"
                  target="_blank"
                >
                  Download Low-Res Image
                </Button>

                {showCoverImageButton && (
                  <Button
                    buttonStyle="secondaryUtility"
                    icon={<FontAwesomeIcon icon={faStar} />}
                    marginTop="medium"
                    onClick={() => setCoverImage(coverImagePayload)}
                    size="medium"
                  >
                    Make Cover Image
                  </Button>
                )}
              </Form.Field>
            </>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          loading={updating}
          onClick={() => closeModal()}
          size="large"
        >
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditImageModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditImageModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
